import { cn } from '@/lib/utils';

import ActivityCentre from '../ActivityCentre';
import { SidebarExpandable } from './SidebarExpandable';
import { SidebarNavItem } from './SidebarNavItem';
import type { NavSection } from './types';

interface SidebarSectionProps {
  section: NavSection;
  isFirst: boolean;
  collapsed: boolean;
  pathname: string;
}

// One sidebar group: an optional heading (replaced by a hairline divider when
// collapsed) plus its items. Headings match the row label size (Figma draws
// both at the same P1 size, differentiated by grey vs off-black only) — on
// the tuned 14px ramp that means text-sm for both. px-4 aligns heading text
// with the row icons (both 12px inset).
export function SidebarSection({
  section,
  isFirst,
  collapsed,
  pathname,
}: SidebarSectionProps) {
  return (
    <nav className={cn('px-4', section.heading && !isFirst && 'mt-6')}>
      {section.heading &&
        (collapsed ? (
          <div className="h-px bg-filled-2 mx-3 mb-2" />
        ) : (
          <div className="text-sm font-semibold text-text-secondary px-4 mb-2">
            {section.heading}
          </div>
        ))}

      <ul className="space-y-1">
        {section.items.map((item, index) => {
          switch (item.kind) {
            case 'expandable':
              return (
                <li key={item.label}>
                  <SidebarExpandable
                    item={item}
                    collapsed={collapsed}
                    pathname={pathname}
                  />
                </li>
              );
            case 'react-mount':
              // ActivityCentre reads its translations from the sidebar's
              // TranslationProvider (set up at mount time). collapsed is
              // threaded so its label and count hide on the icon rail, like
              // every other nav item.
              return (
                <li key={`react-${item.component}`}>
                  <ActivityCentre collapsed={collapsed} />
                </li>
              );
            case 'link':
              return (
                <SidebarNavItem
                  key={`${item.label}-${index}`}
                  item={item}
                  collapsed={collapsed}
                  pathname={pathname}
                />
              );
          }
        })}
      </ul>
    </nav>
  );
}
